import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import Grid2 from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { SearchTextField } from "@/controls/SearchTextField";
import { CatalogGrade } from "@/shared/models/CatalogGrade";
import { useCatalogGrades } from "../CatalogGradesPage";
import { useTableData } from "../hooks/useTableData";
import { UpsertDeleteModal } from "./UpsertDeleteModal";

export const TableGrades = () => {
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [selectedRowData, setSelectedRowData] = useState<CatalogGrade | null>(
		null,
	);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [error, setError] = useState("");
	const [quickFilterText, setQuickFilterText] = useState("");

	const { data: rows, isLoading } = useCatalogGrades({
		onError: (error) => setError(error.message),
	});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs, getMenuOptions } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowDoubleClicked = (event: any) => {
		setSelectedRowData(event.data);
		setShowModal("update");
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "gradesContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	useEffect(() => {
		registerConfig({
			id: "gradesContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2
					container
					size={{ md: 12, xs: 12 }}
					spacing={1}
					direction={"column"}
				>
					<Grid2>
						<Typography variant="h5" fontWeight={600}>
							Grades
						</Typography>
					</Grid2>
				</Grid2>
				<Grid2 container justifyContent="center" alignItems="center">
					<Grid2 size={{ md: 12, xs: 12 }} style={{ marginBottom: 10 }}>
						<SearchTextField
							value={quickFilterText}
							onChange={(text) => setQuickFilterText(text)}
							label="Search grades"
						/>
					</Grid2>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Paper elevation={2} style={{ height: "68vh", width: "100%" }}>
							<AgGridReact<CatalogGrade>
								loading={isLoading}
								rowData={rows || []}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={30}
								animateRows
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={onRowDoubleClicked}
								onGridReady={(params) => setGridApi(params.api)}
								loadingOverlayComponent={CenteredLazyLoading}
								getRowId={(params) => `${params.data.GradeId}`}
								quickFilterText={quickFilterText}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: true,
									},
								}}
							/>
						</Paper>
					</Grid2>
					<FooterButton
						label="GRADE"
						showCreateModal={showModal === "create"}
						setShowCreateModal={(showCreateModal) =>
							showCreateModal ? setShowModal("create") : setShowModal("")
						}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<UpsertDeleteModal
				grade={selectedRowData}
				modal={showModal}
				onHide={() => {
					setShowModal("");
				}}
			/>
		</>
	);
};
